import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

export default function ModalAgendamento({
  aberto,
  onClose,
  slot,
  user,
  babaId,
  vendaSelecionada,
  saldoHoras,
  valorHora,
  nomeBaba,
  onSuccess,
}) {
  const [duracao, setDuracao] = useState(2);
  const [loading, setLoading] = useState(false);
  const [saldo, setSaldo] = useState(null);

  // Sincroniza o saldo inicial vindo das props
  useEffect(() => {
    setSaldo(saldoHoras);
  }, [saldoHoras]);

  useEffect(() => {
    const carregarSaldo = async () => {
      if (!user?.id) return;
      const { data, error } = await supabase
        .from("saldo_horas")
        .select("horas_restantes")
        .eq("user_id", user.id);

      if (!error && data) {
        const totalRestante = data.reduce(
          (acc, curr) => acc + (Number(curr.horas_restantes) || 0),
          0,
        );
        setSaldo(totalRestante);
      }
    };

    if (aberto) {
      carregarSaldo();
    }
  }, [aberto, user?.id]);

  if (!aberto || !slot) return null;

  const horasDisponiveis = Number(saldo) ?? 0;

  // 🔥 Garantir que a hora de início é uma cópia independente do que foi clicado
  const horaInicio = new Date(slot.start);

  const podeAgendar = duracao <= horasDisponiveis;

  // 🔥 Calcular a hora final somando a duração ao timestamp da hora de início
  const horaFim = new Date(
    horaInicio.getTime() + Number(duracao) * 60 * 60 * 1000,
  );

  const confirmarAgendamento = async () => {
    if (!podeAgendar) return;
    setLoading(true);

    try {
      const { data, error } = await supabase.rpc("agendar_baba", {
        p_familia_id: user.id,
        p_baba_id: babaId,
        p_inicio: horaInicio.toISOString(),
        p_fim: horaFim.toISOString(),
        p_venda_id: vendaSelecionada,
      });

      if (error) throw error;

      if (data?.erro) {
        alert("❌ " + data.erro);
      } else {
        alert("✅ Agendamento solicitado!");
        if (onSuccess) onSuccess();
        onClose(); // Fecha o modal após o sucesso
      }
    } catch (err) {
      console.error(err);
      alert("Erro ao processar agendamento.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-end justify-center bg-black/40">
      <div className="w-full max-w-[500px] animate-[slideUp_0.25s_ease] rounded-t-[20px] bg-white p-4">
        {/* Header */}
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-bold text-warm-900">Confirmar reserva</h2>
          <button
            className="flex h-7 w-7 items-center justify-center rounded-lg text-warm-400 hover:bg-warm-100 hover:text-warm-700 transition-colors"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {/* Babá */}
        <div className="mb-2.5 font-semibold text-warm-800">👶 {nomeBaba}</div>

        {/* Info */}
        <div className="mb-3 rounded-xl bg-warm-50 p-3 text-sm text-warm-700 space-y-1">
          <p><strong>Data:</strong> {horaInicio.toLocaleDateString()}</p>
          <p>
            <strong>Início:</strong>{" "}
            {horaInicio.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </p>
        </div>

        {/* Duração */}
        <div className="mb-3">
          <p className="mb-2 text-sm font-medium text-warm-700">Duração:</p>
          <div
            className="grid gap-2"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(50px, 1fr))" }}
          >
            {Array.from(
              { length: Math.max(1, Math.min(horasDisponiveis, 12)) },
              (_, i) => i + 1,
            ).map((h) => (
              <button
                key={h}
                className={[
                  "rounded-xl border py-2.5 text-sm font-medium transition-colors",
                  duracao === h
                    ? "border-rose-400 bg-rose-400 text-white"
                    : "border-warm-200 bg-warm-50 text-warm-700 hover:border-rose-300",
                ].join(" ")}
                onClick={() => setDuracao(h)}
              >
                {h}h
              </button>
            ))}
          </div>
        </div>

        {/* Resumo de saldo */}
        <div className="mb-3 flex justify-between rounded-xl bg-warm-50 px-4 py-3 text-sm text-warm-700">
          <div className="space-y-0.5">
            <p>Saldo atual: <strong>{horasDisponiveis}h</strong></p>
            <p>Duração selecionada: <strong>{duracao}h</strong></p>
          </div>
          <div>
            <p>Saldo após reserva: <strong>{Math.max(0, horasDisponiveis - duracao)}h</strong></p>
          </div>
        </div>

        {!podeAgendar && (
          <p className="mb-3 text-[13px] text-rose-500">
            ⚠️ Você não possui saldo suficiente para este período.
          </p>
        )}

        {/* Ações */}
        <div className="flex gap-3">
          <button
            className="flex-1 rounded-xl border border-warm-200 bg-warm-100 py-3 text-sm font-medium text-warm-700 hover:bg-warm-200 disabled:opacity-50 transition-colors"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            className="flex-1 rounded-xl bg-emerald-500 py-3 text-sm font-bold text-white hover:bg-emerald-600 disabled:opacity-50 transition-colors"
            disabled={!podeAgendar || loading}
            onClick={confirmarAgendamento}
          >
            {loading ? "Processando..." : "Confirmar Agendamento"}
          </button>
        </div>
      </div>
    </div>
  );
}
